import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Map, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { db } from '../../firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { FiTrash2, FiRotateCcw, FiSave, FiTarget, FiMap, FiHome, FiCrosshair } from 'react-icons/fi';

const COURSE_COLORS = [
  { id: 'red', color: '#FF0000', label: 'Piros pálya' },
  { id: 'blue', color: '#0000FF', label: 'Kék pálya' },
  { id: 'green', color: '#008000', label: 'Zöld pálya' },
  { id: 'yellow', color: '#FFD700', label: 'Sárga pálya' },
  { id: 'purple', color: '#800080', label: 'Lila pálya' }
];

// SEGÉDFÜGGVÉNY: Távolság számítás (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lng2) => {
  const R = 6371e3; // Föld sugara méterben
  const phi1 = lat1 * Math.PI / 180;
  const phi2 = lat2 * Math.PI / 180;
  const deltaPhi = (lat2 - lat1) * Math.PI / 180;
  const deltaLambda = (lng2 - lon1) * Math.PI / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Méterben
};

const CourseMapEditor = forwardRef(({ competitionId, numberOfTargets, defaultLat, defaultLng, onDistanceChange, totalDistance }, ref) => {
  const map = useMap();
  const mapsLib = useMapsLibrary('maps');

  const [activeColor, setActiveColor] = useState(COURSE_COLORS[0]);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false); // ÚJ: Rajzoló mód kapcsoló
  const [paths, setPaths] = useState([]); // [{ color, points: [{lat, lng}, ...] }]
  const [targets, setTargets] = useState([]); // [{ id, lat, lng }]
  const [specialMarkers, setSpecialMarkers] = useState({ // ÚJ: Speciális jelölők
    office: { lat: null, lng: null },
    warmup: { lat: null, lng: null }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // EXPORTÁLT FUNKCIÓK A SZÜLŐNEK (Nyomtatáshoz)
  useImperativeHandle(ref, () => ({
    fitAllElements: () => {
      if (!map || !window.google) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasElements = false;

      // Utak hozzáadása a kerethez
      paths.forEach(path => {
        path.points.forEach(pt => {
          bounds.extend(pt);
          hasElements = true;
        });
      });

      // Célok hozzáadása
      targets.forEach(t => {
        if (t.lat && t.lng) {
          bounds.extend({ lat: t.lat, lng: t.lng });
          hasElements = true;
        }
      });

      // Speciális pontok
      if (specialMarkers.office.lat) {
        bounds.extend(specialMarkers.office);
        hasElements = true;
      }
      if (specialMarkers.warmup.lat) {
        bounds.extend(specialMarkers.warmup);
        hasElements = true;
      }

      if (hasElements) {
        map.fitBounds(bounds, {
            top: 50, right: 50, bottom: 50, left: 50
        });
      }
    }
  }));

  // Össztávolság számítása és visszajelzés a szülőnek
  useEffect(() => {
    let totalDist = 0;
    paths.forEach(path => {
      for (let i = 0; i < path.points.length - 1; i++) {
        totalDist += calculateDistance(
          path.points[i].lat, path.points[i].lng,
          path.points[i+1].lat, path.points[i+1].lng
        );
      }
    });

    if (onDistanceChange) {
      onDistanceChange(Math.round(totalDist));
    }
  }, [paths, onDistanceChange]);

  // Referenciák a Google Maps objektumokhoz
  const polylinesRef = useRef([]);

  // Kezdőpont meghatározása (vagy a verseny GPS-e, vagy Magyarország közepe)
  const initialCenter = useMemo(() => {
    const lat = parseFloat(defaultLat);
    const lng = parseFloat(defaultLng);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0) {
      return { lat, lng };
    }
    return { lat: 47.1625, lng: 19.5033 };
  }, [defaultLat, defaultLng]);

  // 1. ADATOK BETÖLTÉSE FIRESTORE-BÓL
  useEffect(() => {
    if (!competitionId) return;

    const unsub = onSnapshot(doc(db, 'events', competitionId), (docSnap) => {
      if (docSnap.exists()) {
        const eventData = docSnap.data();
        const data = eventData.courseMapData || {};
        setPaths(data.paths || []);
        setSpecialMarkers(data.specialMarkers || { office: { lat: null, lng: null }, warmup: { lat: null, lng: null } });

        const savedTargets = data.targets || [];
        if (savedTargets.length === 0) {
            const initialTargets = Array.from({ length: numberOfTargets || 0 }, (_, i) => ({
                id: i + 1,
                lat: null,
                lng: null
            }));
            setTargets(initialTargets);
        } else {
            // Ha nőt a célszám, hozzáadjuk az újakat
            if (savedTargets.length < numberOfTargets) {
              const updatedTargets = [...savedTargets];
              for (let i = savedTargets.length + 1; i <= numberOfTargets; i++) {
                updatedTargets.push({ id: i, lat: null, lng: null });
              }
              setTargets(updatedTargets);
            } else {
              setTargets(savedTargets);
            }
        }
      }
      setLoading(false);
    });

    return () => unsub();
  }, [competitionId, numberOfTargets]);

  // 2. VONALAK RAJZOLÁSA A TÉRKÉPRE
  useEffect(() => {
    if (!map || !mapsLib) return;

    // Régi vonalak eltávolítása
    polylinesRef.current.forEach(p => p.setMap(null));
    polylinesRef.current = [];

    // Új vonalak létrehozása
    paths.forEach(pathData => {
      const polyline = new mapsLib.Polyline({
        path: pathData.points,
        geodesic: true,
        strokeColor: pathData.color,
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: map
      });
      polylinesRef.current.push(polyline);
    });
  }, [paths, map, mapsLib]);

  // 3. KATTINTÁS KEZELÉSE (PONT LERAKÁSA / SNAPPING)
  const handleMapClick = useCallback((e) => {
    if (!isDrawingEnabled || !e.detail.latLng) return;

    // SNAPPING LOGIKA: Ha egy meglévő pontra (markerre) kattintunk,
    // a Google Maps detail.latLng-je már a marker koordinátája lesz.
    // De biztosítjuk, hogy ne nyelje el a kattintást.

    const newPoint = {
      lat: e.detail.latLng.lat,
      lng: e.detail.latLng.lng
    };

    setPaths(prev => {
      const existingPathIdx = prev.findIndex(p => p.color === activeColor.color);

      if (existingPathIdx >= 0) {
        const updated = [...prev];
        // Ellenőrizzük, hogy nem ugyanaz-e a pont (duplikáció szűrés)
        const lastPoint = updated[existingPathIdx].points[updated[existingPathIdx].points.length - 1];
        if (lastPoint && lastPoint.lat === newPoint.lat && lastPoint.lng === newPoint.lng) {
            return prev;
        }

        updated[existingPathIdx] = {
          ...updated[existingPathIdx],
          points: [...updated[existingPathIdx].points, newPoint]
        };
        return updated;
      } else {
        return [...prev, { color: activeColor.color, points: [newPoint] }];
      }
    });
  }, [isDrawingEnabled, activeColor]);

  // 4. CÉL / SPECIÁLIS JELÖLŐ MOZGATÁSA
  const handleTargetDragEnd = (id, e, type = 'target') => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();

    if (type === 'target') {
      setTargets(prev => prev.map(t =>
        t.id === id ? { ...t, lat: newLat, lng: newLng } : t
      ));
    } else {
      setSpecialMarkers(prev => ({
        ...prev,
        [type]: { lat: newLat, lng: newLng }
      }));
    }
  };

  // 5. MENTÉS
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'events', competitionId), {
        courseMapData: {
          paths,
          targets,
          specialMarkers,
          updatedAt: new Date().toISOString()
        }
      });
      alert("Pályatérkép sikeresen mentve! ✅");
    } catch (err) {
      console.error("Error saving map:", err);
      alert("Hiba történt a mentés során.");
    } finally {
      setSaving(false);
    }
  };

  // 6. TÖRLÉS / UNDO
  const handleUndo = () => {
    setPaths(prev => {
      const idx = prev.findIndex(p => p.color === activeColor.color);
      if (idx < 0) return prev;

      const updated = [...prev];
      const newPoints = [...updated[idx].points];
      newPoints.pop();

      if (newPoints.length === 0) {
        updated.splice(idx, 1);
      } else {
        updated[idx] = { ...updated[idx], points: newPoints };
      }
      return updated;
    });
  };

  const handleClearMap = () => {
    if (window.confirm(`Biztosan törölni szeretnél minden útvonalat és jelölőt a térképről?`)) {
      setPaths([]);
      setTargets(prev => prev.map(t => ({ ...t, lat: null, lng: null })));
      setSpecialMarkers({
        office: { lat: null, lng: null },
        warmup: { lat: null, lng: null }
      });
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Térkép adatok betöltése...</div>;

  return (
    <div style={containerStyle} className="force-light-theme map-editor-main-container">

      {/* BAL OLDAL: TÉRKÉP */}
      <div style={mapContainerStyle} className="map-print-container">
        <Map
          defaultCenter={initialCenter}
          defaultZoom={17}
          mapId="COURSE_EDITOR_MAP"
          mapTypeId="satellite"
          onClick={handleMapClick}
          style={{ width: '100%', height: '100%' }}
          gestureHandling={'greedy'}
          disableDefaultUI={true} // ÚJ: Minden alapértelmezett gomb elrejtése
        >
          {/* CÉL JELÖLŐK RENDERE */}
          {targets.map(target => (
            target.lat && (
              <AdvancedMarker
                key={`target-${target.id}`}
                position={{ lat: target.lat, lng: target.lng }}
                draggable={!isDrawingEnabled}
                onDragEnd={(e) => handleTargetDragEnd(target.id, e, 'target')}
              >
                <div style={targetMarkerStyle}>
                  <div style={targetLabelStyle}>{target.id}</div>
                  <div style={pinPointStyle} />
                </div>
              </AdvancedMarker>
            )
          ))}

          {/* SPECIÁLIS JELÖLŐK: IRODA */}
          {specialMarkers.office.lat && (
            <AdvancedMarker
              position={specialMarkers.office}
              draggable={!isDrawingEnabled}
              onDragEnd={(e) => handleTargetDragEnd('office', e, 'office')}
            >
              <div style={targetMarkerStyle}>
                <div style={{ ...targetLabelStyle, color: '#007bff', borderColor: '#007bff' }}>IRODA</div>
                <div style={{ ...pinPointStyle, backgroundColor: '#007bff' }}>
                  <FiHome size={10} color="white" style={{ transform: 'rotate(45deg)', marginBottom: '4px' }} />
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* SPECIÁLIS JELÖLŐK: BELÖVŐ */}
          {specialMarkers.warmup.lat && (
            <AdvancedMarker
              position={specialMarkers.warmup}
              draggable={!isDrawingEnabled}
              onDragEnd={(e) => handleTargetDragEnd('warmup', e, 'warmup')}
            >
              <div style={targetMarkerStyle}>
                <div style={{ ...targetLabelStyle, color: '#333', borderColor: '#333' }}>BELÖVŐ</div>
                <div style={{ ...pinPointStyle, backgroundColor: '#333' }}>
                  <FiCrosshair size={10} color="white" style={{ transform: 'rotate(45deg)', marginBottom: '4px' }} />
                </div>
              </div>
            </AdvancedMarker>
          )}

          {/* ÚJ: ÚTVONAL PONTOK (VERTICES) MEGJELENÍTÉSE RAJZOLÁS KÖZBEN */}
          {isDrawingEnabled && paths.map((pathData, pIdx) => (
            pathData.points.map((point, ptIdx) => (
              <AdvancedMarker
                key={`vertex-${pIdx}-${ptIdx}`}
                position={point}
                zIndex={100}
                onClick={(e) => {
                  // MÁGNESES SNAPPING: Megállítjuk a térkép kattintását, és a PONTOS koordinátát küldjük tovább
                  if (e.domEvent) {
                    e.domEvent.stopPropagation();
                    e.domEvent.preventDefault();
                  }
                  handleMapClick({ detail: { latLng: point } });
                }}
              >
                <div style={{
                  width: '14px',
                  height: '14px',
                  backgroundColor: pathData.color,
                  borderRadius: '50%',
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer'
                }} />
              </AdvancedMarker>
            ))
          ))}
        </Map>

        {/* TÁVOLSÁG KIJELZŐ A TÉRKÉPEN */}
        {totalDistance > 0 && (
          <div style={distanceOverlayStyle}>
            Teljes táv: <strong>{totalDistance} m</strong>
          </div>
        )}

        {/* GYORSGOMBOK A TÉRKÉPEN */}
        <div style={mapOverlayControlsStyle}>
            <button style={toolButtonStyle} onClick={handleUndo}>
                <FiRotateCcw /> Vissza
            </button>
            <button style={toolButtonStyle} onClick={handleClearMap}>
                <FiTrash2 /> Térkép ürítése
            </button>
        </div>
      </div>

      {/* JOBB OLDAL: KEZELŐPANEL */}
      <div style={sidebarStyle}>

        {/* ÜZEMMÓD VÁLASZTÓ */}
        <div style={panelSectionStyle}>
          <div style={panelLabelStyle}>Munkamód</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsDrawingEnabled(true)}
              style={{
                ...modeButtonStyle,
                backgroundColor: isDrawingEnabled ? '#b30000' : '#f5f5f5',
                color: isDrawingEnabled ? '#fff' : '#666',
                borderColor: isDrawingEnabled ? '#b30000' : '#ddd'
              }}
            >
              <FiMap size={16} /> Rajzolás
            </button>
            <button
              onClick={() => setIsDrawingEnabled(false)}
              style={{
                ...modeButtonStyle,
                backgroundColor: !isDrawingEnabled ? '#2E7D32' : '#f5f5f5',
                color: !isDrawingEnabled ? '#fff' : '#666',
                borderColor: !isDrawingEnabled ? '#2E7D32' : '#ddd'
              }}
            >
              <FiTarget size={16} /> Célok
            </button>
          </div>
        </div>

        <div style={dividerStyle} />

        {/* SZÍNVÁLASZTÓ */}
        <div style={panelSectionStyle}>
          <div style={panelLabelStyle}>Pálya színe:</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {COURSE_COLORS.map(c => (
              <div
                key={c.id}
                onClick={() => setActiveColor(c)}
                style={{
                  ...colorCircleStyle,
                  backgroundColor: c.color,
                  border: activeColor.id === c.id ? '4px solid #333' : '2px solid transparent',
                  transform: activeColor.id === c.id ? 'scale(1.1)' : 'none'
                }}
                title={c.label}
              />
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: activeColor.color }}>
             {activeColor.label} rajzolása aktív
          </div>
        </div>

        <div style={dividerStyle} />

        {/* SPECIÁLIS JELÖLŐK LISTÁJA */}
        <div style={panelSectionStyle}>
          <div style={panelLabelStyle}>Speciális helyszínek:</div>
          <div style={{ display: 'flex', gap: '8px' }}>
             <button
                onClick={() => {
                  const center = map.getCenter();
                  handleTargetDragEnd('office', { latLng: center }, 'office');
                }}
                style={{
                  ...targetItemStyle,
                  backgroundColor: specialMarkers.office.lat ? '#E3F2FD' : '#f5f5f5',
                  borderColor: specialMarkers.office.lat ? '#007bff' : '#ddd',
                  color: specialMarkers.office.lat ? '#007bff' : '#666',
                  flex: 1
                }}
             >
                🏢 Versenyiroda
             </button>
             <button
                onClick={() => {
                  const center = map.getCenter();
                  handleTargetDragEnd('warmup', { latLng: center }, 'warmup');
                }}
                style={{
                  ...targetItemStyle,
                  backgroundColor: specialMarkers.warmup.lat ? '#EEEEEE' : '#f5f5f5',
                  borderColor: specialMarkers.warmup.lat ? '#333' : '#ddd',
                  color: specialMarkers.warmup.lat ? '#333' : '#666',
                  flex: 1
                }}
             >
                🏹 Belövő pálya
             </button>
          </div>
        </div>

        {/* CÉLOK LISTÁJA */}
        <div style={{ ...panelSectionStyle, flex: 1, overflow: 'hidden' }}>
          <div style={panelLabelStyle}>Célok ({numberOfTargets} db):</div>
          <div style={targetsListStyle}>
            {targets.map(t => (
              <div
                key={t.id}
                onClick={() => {
                    if (isDrawingEnabled) return;
                    if (!t.lat) {
                        const center = map.getCenter();
                        handleTargetDragEnd(t.id, { latLng: center }, 'target');
                    } else {
                        map.panTo({ lat: t.lat, lng: t.lng });
                    }
                }}
                style={{
                  ...targetItemStyle,
                  backgroundColor: t.lat ? '#E8F5E9' : '#f5f5f5',
                  borderColor: t.lat ? '#2E7D32' : '#ddd',
                  color: t.lat ? '#2E7D32' : '#666',
                  opacity: isDrawingEnabled ? 0.5 : 1,
                  textAlign: 'center',
                  minWidth: '60px'
                }}
              >
                {t.id}.
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{ ...saveButtonStyle, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Mentés...' : <><FiSave /> Térkép Mentése</>}
        </button>
      </div>
    </div>
  );
});

const containerStyle = {
  display: 'flex',
  flexDirection: 'row',
  height: 'calc(100vh - 180px)',
  gap: '20px',
  backgroundColor: '#f8f9fa'
};

const mapContainerStyle = {
  flex: 1,
  borderRadius: '15px',
  overflow: 'hidden',
  border: '1px solid #ddd',
  position: 'relative',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
};

const sidebarStyle = {
  width: '320px',
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '15px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  overflowY: 'auto'
};

const dividerStyle = {
  height: '1px',
  backgroundColor: '#eee',
  margin: '5px 0'
};

const panelSectionStyle = { display: 'flex', flexDirection: 'column', gap: '8px' };
const panelLabelStyle = { fontSize: '0.7rem', fontWeight: 'bold', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' };
const colorCircleStyle = { width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent' };
const targetsListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(65px, 1fr))',
  gap: '8px',
  padding: '5px',
  overflowY: 'auto'
};

const modeButtonStyle = {
  flex: 1,
  padding: '10px',
  borderRadius: '10px',
  border: '2px solid',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  transition: 'all 0.2s'
};

const targetItemStyle = {
  padding: '8px',
  borderRadius: '10px',
  border: '2px solid',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const saveButtonStyle = {
  backgroundColor: '#b30000',
  color: 'white',
  border: 'none',
  padding: '15px',
  borderRadius: '12px',
  fontWeight: 'bold',
  fontSize: '1rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  marginTop: 'auto',
  boxShadow: '0 4px 12px rgba(179,0,0,0.3)'
};

const mapOverlayControlsStyle = {
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '10px',
  zIndex: 1
};

const toolButtonStyle = {
    backgroundColor: 'rgba(255,255,255,0.95)',
    border: '1px solid #ddd',
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
};

const distanceOverlayStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#b30000',
    padding: '8px 15px',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 'bold',
    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
    zIndex: 5,
    border: '2px solid #b30000'
};

const targetMarkerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translateY(-50%)' };

const targetLabelStyle = {
  backgroundColor: '#fff',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '0.8rem',
  fontWeight: '900',
  color: '#b30000',
  border: '2px solid #b30000',
  marginBottom: '2px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  zIndex: 1
};

const pinPointStyle = {
    width: '16px',
    height: '16px',
    backgroundColor: '#D32F2F',
    borderRadius: '50% 50% 50% 0',
    transform: 'rotate(-45deg) translateY(-2px) translateX(2px)',
    border: '2px solid white',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    marginTop: '-4px'
};

export default CourseMapEditor;
